// @ts-strict-ignore
import { createPayloadAction } from '@r/helpers/redux-helpers'
import { FsCore } from '@t/fs-core'
import { readCookieRaw } from '@u/cookies'
import { getData } from '@r/elasticsearch/elasticsearch-actions'
import uuidv4 from '@u/guid'
import { setBadCreditCardResult, setHasUserCompletedEC } from '@r/lead-form/lead-form-actions'

const { __FS_API_URL__ } = global

export interface SetProductTablePayload {
  id: string
  productTable: FsCore.ESProductTableState
}
export const setProductTable = createPayloadAction<SetProductTablePayload>('SET_PRODUCT_TABLE')
export interface UpdateProductTablePayload {
  id: string
  pageOffset: number
  selectedSort: FsCore.SortState
}
export const updateProductTable =
  createPayloadAction<UpdateProductTablePayload>('UPDATE_PRODUCT_TABLE')

export interface UpdateProductTableEligibilityPayload {
  id: string
  eligibilityResults: any
}

export const updateProductTableEligibility =
  createPayloadAction<UpdateProductTableEligibilityPayload>('UPDATE_PRODUCT_TABLE_ELIGIBILITY')

export const setIsLoading = createPayloadAction<{ id: string; isLoading: boolean }>(
  'SET_IS_LOADING',
)
export const setPageOffset = createPayloadAction<{ id: string; pageOffset: number }>(
  'SET_PAGE_OFFSET',
)

export const eligibilityRequest = (
  brand: string,
  showZeroPercent: boolean = true,
  minEligibility: number = 0,
  maxEligibility: number = 100,
) => {
  return fetch(
    `${__FS_API_URL__}/eligibility?brand=${brand}&deviceId=${readCookieRaw(
      'uscd',
    )}&requestId=${uuidv4()}&zeroEligibility=${showZeroPercent}&minEligibility=${minEligibility}&maxEligibility=${maxEligibility}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  )
}

// eslint-disable-next-line no-magic-numbers
const ONE_DAY = 24 * 60 * 60 * 1000
export const staleResults = (results: FsCore.EligibilityResults) =>
  Date.now() - results.createdDate >= ONE_DAY

export const getCreditCardEligibilityInformation = ({
  brand,
  datasetID,
  productTableId,
  showZeroPercent,
  minEligibility,
  maxEligibility,
}: any) => {
  const redirectToPreEligibility = () => {
    const currentUrl = window.location.href
    // TODO we might need a fallback url, in case the current url doesn't end with /results
    const redirectUrl =
      brand === 'money'
        ? currentUrl.replace('/results', '.htm')
        : currentUrl.replace('/results', '')
    window.location.href = redirectUrl
  }

  const NOT_FOUND = 400
  const SERVER_ERROR = 500

  const noErrorsNoResults = (results: FsCore.EligibilityResults) =>
    (!results.errors || (results.errors && results.errors.length === 0)) &&
    results.results &&
    results.results.length === 0

  const fetchEligibility = async () => {
    const response = await eligibilityRequest(
      brand,
      showZeroPercent,
      minEligibility,
      maxEligibility,
    )

    if (response.status > NOT_FOUND && response.status < SERVER_ERROR) {
      redirectToPreEligibility()
    } else if (response.status >= SERVER_ERROR) {
      throw new Error('Error from FS-API')
    }

    const eligibilityResults: FsCore.EligibilityResults = await response.json()

    if (noErrorsNoResults(eligibilityResults)) {
      return { ...eligibilityResults, noEligibilityResults: true }
    }

    if (staleResults(eligibilityResults)) {
      return {
        ...eligibilityResults,
        results: undefined,
        expired: true,
      }
    } else {
      return eligibilityResults
    }
  }

  const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

  const eligibilityWithRetry = async () => {
    const timeLimit = 30000 // ms
    const waitFor = 200 // ms

    let totalTime = 0
    let then

    while (totalTime < timeLimit) {
      then = Date.now()
      const eligibilityResults: FsCore.EligibilityResults = await fetchEligibility()

      if (eligibilityResults.results) {
        return eligibilityResults
      } else {
        await delay(waitFor)
        totalTime += waitFor + (Date.now() - then)
      }
    }

    return { expired: true, providerTimeout: true, results: undefined }
  }

  return async (dispatch: any, getState: () => FsCore.State) => {
    try {
      const eligibilityResults = await eligibilityWithRetry()

      const sortedEligibilityResults = {
        ...eligibilityResults,
        results: (eligibilityResults?.results || []).sort((a: any, b: any) => {
          return b?.likelihood - a?.likelihood
        }),
      }

      const isCreditScoreBad = () => {
        if (sortedEligibilityResults?.results) {
          return sortedEligibilityResults.results.every(
            (result: any) => result.likelihood === null || result.likelihood < 30,
          )
        } else {
          return false
        }
      }

      dispatch(setBadCreditCardResult({ isCreditScoreBad: isCreditScoreBad() }))
      dispatch(
        setHasUserCompletedEC({
          hasUserCompletedEC:
            sortedEligibilityResults?.results.length > 0 && !sortedEligibilityResults?.expired,
        }),
      )

      dispatch(
        updateProductTableEligibility({
          id: datasetID,
          eligibilityResults: sortedEligibilityResults,
        }),
      )

      await getData({
        productTableId,
        loadingSpinner: false,
      })(dispatch, getState)
    } catch (error) {
      if (window.Sentry) {
        window.Sentry.captureException(error)
      }
    }
  }
}
